// "Updates" — the browsable history of published release notes (What's-New v2 §3).
//
// This is the reopen-after-dismiss gap: the popup fires once per version and, once
// dismissed, there was no way back to it. Two entry points open this ONE panel — the
// "Updates" item in the ☰ menu, and the "See past updates" link on the popup itself — so
// the provider owns the open state and both callers just call openUpdates().
//
// Rendered with ReleaseNoteCard, the SAME component the popup and the admin preview use,
// so a note reads identically wherever it appears.
//
// Provider + hook + panel live in one file on purpose: the state is a single boolean with
// exactly one consumer pair, and a separate context module would be three files for it.
// Mounted inside AuthProvider (App.tsx) — every consumer is already behind auth.

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '../../context/AuthContext';
import { fetchUpdates, type ContentItemOut } from '../../lib/content';
import { C, F, M } from '../ops-board/theme';
import ReleaseNoteCard from './ReleaseNoteCard';

const PAGE_SIZE = 5;

interface UpdatesValue { openUpdates: () => void }
const UpdatesCtx = createContext<UpdatesValue | null>(null);

/** Opens the Updates panel. Safe to call from anywhere inside UpdatesProvider. */
export function useUpdates(): UpdatesValue {
  const ctx = useContext(UpdatesCtx);
  if (!ctx) throw new Error('useUpdates must be used inside UpdatesProvider');
  return ctx;
}

export function UpdatesProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const openUpdates = useCallback(() => setOpen(true), []);
  return (
    <UpdatesCtx.Provider value={{ openUpdates }}>
      {children}
      {open && <UpdatesPanel onClose={() => setOpen(false)} />}
    </UpdatesCtx.Provider>
  );
}

// ─── The panel ────────────────────────────────────────────────────────────────────

function UpdatesPanel({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const [items, setItems] = useState<ContentItemOut[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    // up_to is the RUNNING bundle's version — a note published ahead of its release stays
    // hidden here for the same reason the popup hides it.
    fetchUpdates({ limit: PAGE_SIZE, offset, upTo: __APP_VERSION__ })
      .then((page) => {
        if (cancelled) return;
        setItems(page.items);
        setTotal(page.total);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load updates.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [offset]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!user) return null;

  const from = total === 0 ? 0 : offset + 1;
  const to = offset + items.length;

  return (
    <div
      role="dialog" aria-modal="true" aria-label="Updates"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,41,66,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 320, padding: 20 }}
    >
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, width: '100%', maxWidth: 520, maxHeight: '86vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(15,41,66,0.25)', fontFamily: F }}>

        <div style={{ padding: '16px 20px 12px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <span style={{ fontSize: 15, fontWeight: 800, color: C.text }}>Updates</span>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 7, cursor: 'pointer', color: C.textDim, padding: '2px 9px', fontSize: 14, fontFamily: F }}>✕</button>
        </div>

        <div style={{ padding: '14px 20px 6px', overflowY: 'auto', flex: 1 }}>
          {loading && <div style={{ fontSize: 12, color: C.textMuted, padding: '18px 0' }}>Loading…</div>}

          {!loading && error && (
            <div style={{ background: C.redLight, border: `1px solid ${C.red}`, color: C.red, borderRadius: 8, padding: '10px 12px', fontSize: 12 }}>
              {error}
            </div>
          )}

          {/* A real empty state, not a blank panel that reads as a broken screen. Still
              reachable after the first note shipped: a brand-new tenant has none, and a
              user whose roles match no note's audience_roles sees none either. */}
          {!loading && !error && items.length === 0 && (
            <div style={{ textAlign: 'center', padding: '28px 10px 32px' }}>
              <div style={{ fontSize: 26, marginBottom: 8 }}>📣</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 4 }}>No updates yet</div>
              <div style={{ fontSize: 12, color: C.textMuted, lineHeight: 1.5 }}>
                When a new version of AvSuite ships, the release notes for it appear here.
              </div>
            </div>
          )}

          {!loading && !error && items.map((it, i) => (
            <div key={it.id} style={{ paddingBottom: 14, marginBottom: 14, borderBottom: i === items.length - 1 ? 'none' : `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 8, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: M, fontSize: 12, color: '#fff', background: C.accent, padding: '2px 8px', borderRadius: 6, fontWeight: 700 }}>
                  v{it.version ?? '—'}
                </span>
                {/* The list is ordered by VERSION, not by this date — a note re-published
                    under its original publication date sits where its version says it
                    belongs, so the date is labelled rather than left to be inferred. */}
                {it.published_at && (
                  <span style={{ fontSize: 11, color: C.textMuted }}>
                    Published {new Date(it.published_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                )}
              </div>
              <ReleaseNoteCard data={it} assets={it.assets ?? []} />
            </div>
          ))}
        </div>

        {!loading && !error && total > 0 && (
          <div style={{ padding: '10px 20px 16px', borderTop: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <span style={{ fontSize: 11, color: C.textMuted }}>
              {from}–{to} of {total}, newest first
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button
                onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))} disabled={offset === 0} aria-label="Newer"
                style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, cursor: offset === 0 ? 'default' : 'pointer', color: C.textDim, padding: '3px 10px', opacity: offset === 0 ? 0.4 : 1, fontFamily: F }}
              >‹ Newer</button>
              <button
                onClick={() => setOffset((o) => o + PAGE_SIZE)} disabled={to >= total} aria-label="Older"
                style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, cursor: to >= total ? 'default' : 'pointer', color: C.textDim, padding: '3px 10px', opacity: to >= total ? 0.4 : 1, fontFamily: F }}
              >Older ›</button>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
